import React, { useCallback, useEffect, useState } from 'react';
import type { AjaxError } from 'rxjs';
import { fetchExternalLinks } from 'rest/external-links';
import { getErrorData, trackError } from '@atlassian/help-center-common-util/analytics/listener';
import { useUFOComponentExperience } from '@atlassian/ufo';
import ErrorBoundary from '../error-boundary/error-boundary';
import { externalLinksProductExperience } from './experiences';
import { ExternalLinksStateless, EXTERNAL_LINKS_TO_BE_SHOWN } from './external-links-stateless';

import type { ExternalLinksReponse } from 'rest/external-links/types';

export interface Props {
    projectId: number;
}

export const ExternalLinks: React.FC<Props> = ({ projectId }) => {
    useUFOComponentExperience(externalLinksProductExperience);
    const [isShowAll, setIsShowAll] = useState(false);
    const [links, setLinks] = useState<ExternalLinksReponse['links']>([]);

    useEffect(() => {
        fetchExternalLinks(projectId)
            .toPromise()
            .then((response) => {
                setLinks(response.links);
                if (response.links.length < EXTERNAL_LINKS_TO_BE_SHOWN) {
                    setIsShowAll(true);
                }
            })
            .catch((error: AjaxError) => {
                /**
                 * Send experience failure events only when fetch external links API call responds
                 * with a status of 5xx or 404.
                 */
                if (`${error.status}`.startsWith('5') || error.status === 404) {
                    externalLinksProductExperience
                        .failure({
                            metadata: {
                                error: getErrorData(error),
                            },
                        })
                        .catch((err: Error) => {
                            trackError(
                                'An AJAX error occurred inside the external links experience, but failed to send experience failure event',
                                {},
                                err
                            );
                        });
                }
                setLinks([]);
            });
    }, [projectId]);

    const onShowMoreToggle = useCallback(() => {
        setIsShowAll((val) => {
            return !val;
        });
    }, []);

    const markExperienceFailure = useCallback((error: Error) => {
        // Send experience failure event when JS or HTTP errors occur.
        externalLinksProductExperience
            .failure({
                metadata: {
                    error: getErrorData(error),
                },
            })
            .catch((err: Error) => {
                trackError(
                    'A JS/HTTP error occurred inside the external links experience, but failed to send experience failure event',
                    {},
                    err
                );
            });
    }, []);

    return (
        <ErrorBoundary
            id="jsmPortalExternalResourcesSection"
            packageName="jsmPortalExternalResourcesSection"
            onError={markExperienceFailure}
        >
            <ExternalLinksStateless
                links={isShowAll ? links : links.slice(0, EXTERNAL_LINKS_TO_BE_SHOWN)}
                total={links.length}
                onShowMoreToggle={onShowMoreToggle}
            />
        </ErrorBoundary>
    );
};

export default ExternalLinks;
